import { useEffect, useRef } from "react";
import { persistProgress } from "@/lib/libraryStore";

const COLORS = { teal: "rgba(13,148,136,0.28)", gold: "rgba(201,162,39,0.35)" };

function renderMarked(text, highlights) {
  const hits = highlights
    .map((h) => ({ h, i: text.indexOf(h.text) }))
    .filter((x) => x.i >= 0)
    .sort((a, b) => a.i - b.i);
  const out = [];
  let at = 0;
  hits.forEach(({ h, i }) => {
    if (i < at) return;
    if (i > at) out.push(text.slice(at, i));
    out.push(
      <mark key={h.id} data-testid={`mark-${h.id}`} style={{ background: COLORS[h.color], color: "inherit", borderRadius: 4 }}>
        {h.text}
      </mark>,
    );
    at = i + h.text.length;
  });
  out.push(text.slice(at));
  return out;
}

export const Article = ({ bookId, chapterId, blocks, highlights, startBlock, size, onSelect, onBlockedCopy, onProgressError }) => {
  const ref = useRef(null);

  useEffect(() => {
    const els = ref.current?.querySelectorAll("[data-block]") ?? [];
    if (startBlock > 0 && els[startBlock]) els[startBlock].scrollIntoView({ block: "start" });
    let top = 0;
    let timer;
    let active = true;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) top = Math.max(top, Number(e.target.dataset.block));
      });
      clearTimeout(timer);
      timer = setTimeout(() => {
        persistProgress(bookId, chapterId, top).then(result => {
          if (active && result.error) onProgressError?.();
        });
      }, 600);
    });
    els.forEach((el) => obs.observe(el));
    return () => {
      active = false;
      clearTimeout(timer);
      obs.disconnect();
    };
  }, [bookId, chapterId, startBlock, blocks, onProgressError]);

  const handleSelect = () =>
    setTimeout(() => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !ref.current) return;
      if (!ref.current.contains(sel.anchorNode) || !ref.current.contains(sel.focusNode)) return;
      onSelect(sel.toString().replace(/\s+/g, " ").trim());
    }, 30);

  return (
    <article
      ref={ref}
      data-testid="reader-article"
      onMouseUp={handleSelect}
      onTouchEnd={handleSelect}
      onKeyUp={handleSelect}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => {
        if (String(window.getSelection()).length > 280) {
          e.preventDefault();
          onBlockedCopy();
        }
      }}
      style={{ fontSize: size, lineHeight: 1.7 }}
      className="relative"
    >
      {blocks.map((p, i) => (
        <p key={i} data-block={i} className="m-0 mb-5 [overflow-wrap:anywhere]">
          {renderMarked(p, highlights)}
        </p>
      ))}
    </article>
  );
};
